import { Alert, Icon, LoadingButton, Paper, TextField, Typography } from "@firecms/ui";
import { type AdminUserDocumentReportResult } from "@repo/common";
import { useMutation } from "@tanstack/react-query";
import { useState } from "react";

import AdminToolPage from "components/molecules/AdminToolPage";
import AdminToolReportSection from "components/molecules/AdminToolReportSection";
import { getErrorMessage } from "lib/error.utils";
import { fetchAdminUserDocumentReport } from "services/admin-tools/adminTools";

const TOOL_DESCRIPTION =
  "Nutzer anhand von UID oder E-Mail suchen: Konto (Firebase Auth), App-Profil, Empfehlungen, Favoriten, Fortschritt und Aktivitäten — mit Kurs- und Lektionsnamen statt technischer IDs.";

const AdminUserDocumentReport: React.FC = () => {
  const [identifier, setIdentifier] = useState("");
  const [authJsonExpanded, setAuthJsonExpanded] = useState(false);

  const { data, error, isPending, mutate, reset } = useMutation<AdminUserDocumentReportResult, Error, string>({
    mutationFn: fetchAdminUserDocumentReport,
  });

  const handleSubmit = (): void => {
    const trimmedIdentifier = identifier.trim();
    if (!trimmedIdentifier) {
      return;
    }

    setAuthJsonExpanded(false);
    reset();
    mutate(trimmedIdentifier);
  };

  return (
    <AdminToolPage title="Nutzer-Auswertung" description={TOOL_DESCRIPTION}>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
        <TextField
          label="UID oder E-Mail"
          value={identifier}
          onChange={(event) => {
            setIdentifier(event.target.value);
          }}
          size="medium"
          className="flex-1"
        />
        <LoadingButton loading={isPending} disabled={isPending || !identifier.trim()} onClick={handleSubmit}>
          Auswerten
        </LoadingButton>
      </div>

      {error ? <Alert color="error">{getErrorMessage(error)}</Alert> : null}

      {data ? (
        <div className="flex flex-col gap-4">
          <Typography variant="body2" className="text-gray-600 dark:text-gray-300">
            UID: <code>{data.uid}</code>
          </Typography>

          <AdminToolReportSection title="Konto (Firebase Auth)" emptyMessage="" rows={data.authSummary} />

          <Paper className="overflow-hidden p-0">
            <button
              type="button"
              className="flex w-full cursor-pointer items-center gap-2 border-none bg-transparent p-4 text-left"
              aria-expanded={authJsonExpanded}
              onClick={() => {
                setAuthJsonExpanded((previous) => !previous);
              }}
            >
              <span
                className={`inline-flex shrink-0 transition-transform ${authJsonExpanded ? "rotate-180" : "rotate-0"}`}
                aria-hidden
              >
                <Icon iconKey="expand_more" size="small" />
              </span>
              <Typography variant="body2" className="font-medium">
                Technische Auth-Daten (JSON)
              </Typography>
            </button>

            {authJsonExpanded ? (
              <div className="border-t border-surface-200 px-4 pb-4 pt-3 dark:border-surface-700">
                <pre className="overflow-x-auto whitespace-pre-wrap break-all text-xs">
                  {JSON.stringify(data.authRecord, null, 2)}
                </pre>
              </div>
            ) : null}
          </Paper>

          <AdminToolReportSection title="App-Profil" emptyMessage="" rows={data.profileSummary} />

          <AdminToolReportSection
            title="Empfehlungen"
            emptyMessage="Keine Empfehlungen gespeichert."
            entries={data.recommendations}
          />

          <AdminToolReportSection
            title="Favoriten"
            emptyMessage="Keine Favoriten gespeichert."
            entries={data.favorites}
          />

          <AdminToolReportSection
            title="Angesehen / Fortschritt"
            emptyMessage="Keine angesehenen Inhalte."
            entries={data.watched}
          />

          <AdminToolReportSection
            title={`Letzte Aktivitäten (max. ${String(data.activityLimit)})`}
            emptyMessage="Keine Aktivitäten protokolliert."
            entries={data.activity}
          />
        </div>
      ) : null}
    </AdminToolPage>
  );
};

export default AdminUserDocumentReport;
